import { DOCUMENT, inject, RendererFactory2, Service } from '@angular/core';

declare global {
  interface Window {
    // eslint-disable-next-line custom-rules/prefer-interface-property-readonly
    clarity: Clarity;
  }
}

interface ClarityCommandSignature {
  readonly consentv2: (params: { readonly analytics_Storage: string; }) => void;
  readonly identify: (userId: string, sessionId?: string) => Promise<{ readonly id: string; readonly userHint: string; }>;
}

type ClarityCommand = keyof ClarityCommandSignature;

interface Clarity {
  <Command extends ClarityCommand>(
    command: Command,
    ...params: Parameters<ClarityCommandSignature[Command]>
  ): ReturnType<ClarityCommandSignature[Command]>;
  // eslint-disable-next-line custom-rules/prefer-interface-property-readonly
  q: unknown[];
}

@Service()
export class ClarityService {
  readonly #document = inject(DOCUMENT);
  readonly #renderer2 = inject(RendererFactory2).createRenderer(null, null);

  identify(userId: string): void {
    if (!window.clarity) {
      return;
    }

    if (!userId) {
      console.warn('ClarityService.identify: userId is not defined');
    }

    window.clarity('identify', userId);
    window.clarity('consentv2', {
      // eslint-disable-next-line camelcase
      analytics_Storage: 'granted',
    });
  }

  initialize(key: string): void {
    this.#createClarityScript(key);
  }

  #createClarityScript(key: string): void {
    const script = this.#renderer2.createElement('script') as HTMLScriptElement;
    const scriptSrc = `https://www.clarity.ms/tag/${key}`;

    this.#renderer2.setAttribute(script, 'async', '');
    this.#renderer2.setAttribute(script, 'src', scriptSrc);
    this.#renderer2.appendChild(this.#document.head, script);

    window.clarity = window.clarity || ((...args: unknown[]): void => {
      (window.clarity.q = window.clarity.q || []).push(args);
    });
  }
}
